import axios from "axios";
import dayjs from "dayjs";

// All Rapportmaler / Rapporter calls go through this module.
// Set VUE_APP_API_URL to run against a local API.
export const API_URL = process.env.VUE_APP_API_URL || "https://api.eines-as.no";

const data = (res) => res.data;

// The API stores the sections in `form` ("schema" is reserved in Strapi 3); the components and
// the JSON export use `schema`, same as aquaservices-web-v2.
const withSchema = (entity) => ({ ...entity, schema: entity.form || { sections: [] } });
const toSchema = (res) => (Array.isArray(res.data) ? res.data.map(withSchema) : withSchema(res.data));

export default {
  getTemplates() {
    return axios.get(`${API_URL}/rapportmaler`, { params: { _sort: "name:ASC", _limit: -1 } }).then(toSchema);
  },

  saveTemplate(template) {
    const body = { name: template.name, description: template.description, form: template.schema };
    return template.id
      ? axios.put(`${API_URL}/rapportmaler/${template.id}`, body).then(toSchema)
      : axios.post(`${API_URL}/rapportmaler`, body).then(toSchema);
  },

  deleteTemplate(id) {
    return axios.delete(`${API_URL}/rapportmaler/${id}`).then(data);
  },

  getReports(order) {
    return axios.get(`${API_URL}/rapporter`, { params: { order, _sort: "createdAt:DESC", _limit: -1 } }).then(toSchema);
  },

  // The API copies the template's form into the new report; `data` holds the pre-filled answers.
  createReport(order, template, data) {
    return axios.post(`${API_URL}/rapporter`, { order, rapportmal: template.id, data }).then(toSchema);
  },

  saveReport(id, fields) {
    return axios.put(`${API_URL}/rapporter/${id}`, fields).then(toSchema);
  },

  deleteReport(id) {
    return axios.delete(`${API_URL}/rapporter/${id}`).then(data);
  },

  // /emails answers 200 with success: false when Resend rejects the message.
  sendEmail(email, subject, html) {
    return axios.post(`${API_URL}/emails`, { email, subject, html }).then((res) => {
      if (!res.data || !res.data.success) throw new Error((res.data && res.data.error) || "Sending feilet");
      return res.data;
    });
  },
};

export const FIELD_TYPES = [
  { value: "text", text: "Tekst" },
  { value: "textarea", text: "Lang tekst" },
  { value: "number", text: "Tall" },
  { value: "date", text: "Dato" },
  { value: "time", text: "Tid" },
  { value: "checkbox", text: "Avkrysningsboks" },
  { value: "yesno", text: "Ja / Nei" },
  { value: "select", text: "Nedtrekksliste" },
  { value: "signature", text: "Signatur" },
  { value: "table", text: "Tabell" },
];

export const newField = () => ({ label: "", type: "text", required: false, options: "", columns: "", hint: "", default: "" });

// Field types that can have a default value (checkbox, yes/no and table start empty).
export const DEFAULT_TYPES = ["text", "textarea", "number", "date", "time", "select", "signature"];

const nameOf = (entity) => (entity && entity.name) || "";

// Placeholders a field's default value can use, e.g. "{ordrenummer} – {lokalitet}".
// They are filled in from the order when a report is created.
export const PLACEHOLDERS = [
  { key: "ordretittel", text: "Ordretittel", value: (o) => [o.ordernumber, nameOf(o.customer_company)].filter(Boolean).join(" - ") + (o.site && o.site.name ? " (" + o.site.name + ")" : "") },
  { key: "ordrenummer", text: "Ordrenummer", value: (o) => o.ordernumber },
  { key: "ordretype", text: "Ordretype", value: (o) => o.type },
  { key: "status", text: "Status", value: (o) => o.status },
  { key: "startdato", text: "Startdato", value: (o) => o.startdate, date: true },
  { key: "sluttdato", text: "Sluttdato", value: (o) => o.enddate, date: true },
  { key: "dagens_dato", text: "Dagens dato", value: () => new Date(), date: true },
  { key: "kunde", text: "Kunde", value: (o) => nameOf(o.customer_company) },
  { key: "kundereferanse", text: "Kundens referanse", value: (o) => o.customer_ref },
  { key: "kontaktperson", text: "Kontaktperson", value: (o) => o.contact_name },
  { key: "kontakt_telefon", text: "Kontakt telefon", value: (o) => o.contact_phone },
  { key: "kontakt_epost", text: "Kontakt e-post", value: (o) => o.contact_email },
  { key: "lokalitet", text: "Lokalitet", value: (o) => nameOf(o.site) },
  { key: "lokalitetsnummer", text: "Lokalitetsnummer", value: (o) => o.site && o.site.number },
  { key: "destinasjon", text: "Destinasjon", value: (o) => o.details && nameOf(o.details.destination) },
  { key: "fartøy", text: "Fartøy", value: (o) => nameOf(o.vessel) },
  { key: "skipper", text: "Skipper", value: (o) => nameOf(o.captain) },
  { key: "skift", text: "Skift", value: (o) => nameOf(o.skift) },
  { key: "mannskap", text: "Mannskap", value: (o) => (o.crews || []).map(nameOf).filter(Boolean).join(", ") },
  { key: "selskap", text: "Vårt selskap", value: (o) => nameOf(o.supplier_company) },
];

// Replaces {placeholder}s with order data. Unknown placeholders are left as typed so mistakes are visible.
// Dates are written as DD.MM.YYYY, or YYYY-MM-DD for date fields.
export const fillPlaceholders = (text, order, forDateField = false) =>
  String(text || "").replace(/\{([a-zæøå_]+)\}/gi, (match, key) => {
    const placeholder = PLACEHOLDERS.find((p) => p.key === key.toLowerCase());
    if (!placeholder) return match;
    const value = placeholder.value(order || {});
    if (value === undefined || value === null || value === "") return "";
    if (placeholder.date) return dayjs(value).format(forDateField ? "YYYY-MM-DD" : "DD.MM.YYYY");
    return String(value);
  });

// Answers for a new report: each field's default value with the order's data filled in.
export const fillDefaults = (schema, order) => {
  const answers = {};
  ((schema && schema.sections) || []).forEach((section, si) => {
    (section.fields || []).forEach((field, fi) => {
      if (!field.default || !DEFAULT_TYPES.includes(field.type)) return;
      const value = fillPlaceholders(field.default, order, field.type === "date").trim();
      if (!value) return;
      if (field.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
      if (field.type === "number" && isNaN(Number(value))) return;
      answers[fieldKey(si, fi)] = value;
    });
  });
  return answers;
};

export const hasDefaults = (schema) =>
  ((schema && schema.sections) || []).some((s) => (s.fields || []).some((f) => f.default && DEFAULT_TYPES.includes(f.type)));
export const newSection = () => ({ title: "Ny seksjon", fields: [newField()] });
export const newSchema = () => ({ sections: [newSection()] });

export const splitList = (value) => (value || "").split(",").map((v) => v.trim()).filter(Boolean);

// Answers are keyed "<sectionIndex>_<fieldIndex>" (same as aquaservices-web-v2).
export const fieldKey = (si, fi) => si + "_" + fi;

export const isAnswered = (field, value) => {
  if (field.type === "checkbox") return value === true;
  if (field.type === "yesno") return value === "yes" || value === "no";
  if (field.type === "table") return Array.isArray(value) && value.length > 0;
  return value !== undefined && value !== null && String(value).trim() !== "";
};

export const missingRequired = (schema, answers) => {
  const missing = [];
  ((schema && schema.sections) || []).forEach((section, si) => {
    (section.fields || []).forEach((field, fi) => {
      if (field.required && !isAnswered(field, answers[fieldKey(si, fi)])) {
        missing.push({ si, fi, label: field.label || "(felt uten navn)" });
      }
    });
  });
  return missing;
};

export const countFields = (schema) =>
  ((schema && schema.sections) || []).reduce((n, s) => n + ((s.fields && s.fields.length) || 0), 0);

export const countRequired = (schema) =>
  ((schema && schema.sections) || []).reduce((n, s) => n + (s.fields || []).filter((f) => f.required).length, 0);

const escape = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const displayValue = (field, value) => {
  if (field.type === "checkbox") return value ? "☑ Ja" : "☐ Nei";
  if (field.type === "yesno") return value === "yes" ? "Ja" : value === "no" ? "Nei" : "—";
  if (field.type === "date" && value) return value.split("-").reverse().join(".");
  return value !== undefined && value !== null && value !== "" ? escape(value) : "—";
};

const tableHtml = (field, rows) => {
  const columns = splitList(field.columns);
  if (!Array.isArray(rows) || rows.length === 0) return "—";
  const head = columns.map((c) => `<th>${escape(c)}</th>`).join("");
  const body = rows
    .map((row) => "<tr>" + columns.map((c) => `<td>${escape(row[c] || "")}</td>`).join("") + "</tr>")
    .join("");
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
};

// Self-contained HTML for printing (Save as PDF) and for the e-mail body.
export const reportHtml = (report, order) => {
  const schema = report.schema || { sections: [] };
  const answers = report.data || {};
  const title = escape(report.title || "Rapport");
  const supplier = escape((order.supplier_company && order.supplier_company.name) || "Eines AS");
  const customer = order.customer_company && order.customer_company.name;
  const today = new Date().toLocaleDateString("nb-NO");
  const orderInfo = [order.ordernumber ? "Ordre " + order.ordernumber : "", customer || "", order.site && order.site.name]
    .filter(Boolean)
    .map(escape)
    .join(" · ");
  const status = report.completed
    ? "Fullført" + (report.completed_at ? " " + new Date(report.completed_at).toLocaleDateString("nb-NO") : "")
    : "Påbegynt";

  let html = `<!DOCTYPE html><html lang="nb"><head><meta charset="UTF-8"><title>${title}</title><style>
    body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#1f2937;max-width:800px;margin:0 auto;padding:24px}
    .header{display:flex;justify-content:space-between;border-bottom:3px solid #26334d;padding-bottom:12px;margin-bottom:16px}
    h1{font-size:20px;color:#26334d;margin:0 0 4px 0}
    h2{font-size:14px;color:#26334d;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin:20px 0 10px 0}
    .muted{color:#6b7280;font-size:11px}
    .field{margin-bottom:10px;page-break-inside:avoid}
    .label{font-weight:bold;font-size:11px;color:#374151;text-transform:uppercase}
    .value{white-space:pre-wrap;margin-top:2px}
    table{border-collapse:collapse;width:100%;margin-top:4px}
    th,td{border:1px solid #d1d5db;padding:4px 6px;text-align:left;font-size:11px}
    th{background:#f3f4f6}
    @media print{@page{size:A4;margin:15mm}body{padding:0}}
  </style></head><body>
  <div class="header">
    <div><h1>${title}</h1><div class="muted">${orderInfo}</div><div class="muted">Status: ${status}</div></div>
    <div style="text-align:right"><strong>${supplier}</strong><div class="muted">Dato: ${today}</div></div>
  </div>`;

  schema.sections.forEach((section, si) => {
    html += `<h2>${escape(section.title || "Seksjon " + (si + 1))}</h2>`;
    (section.fields || []).forEach((field, fi) => {
      const value = answers[fieldKey(si, fi)];
      const shown = field.type === "table" ? tableHtml(field, value) : displayValue(field, value);
      html += `<div class="field"><div class="label">${escape(field.label || "")}</div><div class="value">${shown}</div></div>`;
    });
  });

  return html + "</body></html>";
};

export const errorText = (error) =>
  (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
  (error && error.message) ||
  "Noe gikk galt. Prøv igjen.";
